import { useEffect, useState, useRef } from 'react';
import { useAppState } from './contexts/AppStateContext';
import api from '../src/utils/axiosConfig';

const TicketDesign = () => {
    const { currentEvent, currentOrganization } = useAppState();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [design, setDesign] = useState({
        name_x: 50,
        name_y: 150,
        name_size: 60,
        qr_x: 300,
        qr_y: 50,
    });
    const [ticketImage, setTicketImage] = useState(null);

    const containerRef = useRef(null);
    const [activeElement, setActiveElement] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);

    // Fetch ticket design data
    useEffect(() => {
        const fetchTicketDesign = async () => {
            if (!currentEvent || !currentEvent.id) return;

            setLoading(true);
            setError(null);

            try {
                const response = await api.get(`/organizations/${currentOrganization.id}/events/${currentEvent.id}/`);
                
                // Handle the ticket design data - ensure it has all required properties
                const ticketDesign = response.data.ticket_design || {};
                setDesign({
                    name_x: ticketDesign.name_x || 50,
                    name_y: ticketDesign.name_y || 150,
                    name_size: ticketDesign.name_size || 60, // Default to 60 if not present
                    qr_x: ticketDesign.qr_x || 300,
                    qr_y: ticketDesign.qr_y || 50
                });
                setTicketImage(response.data.ticket_image);
                
                setLoading(false);
            } catch (error) {
                console.error('Error fetching ticket design:', error);
                setError('Failed to load ticket design.');
                setLoading(false);
            }
        };

        fetchTicketDesign();
    }, [currentEvent, currentOrganization]);

    const handleFontSizeChange = (e) => {
        const { name, value } = e.target;
        setDesign(prev => ({
            ...prev,
            name_size: parseInt(value, 10)
        }));
    };

    const handleMouseDown = (e, element) => {
        if (!containerRef.current) return;
        
        const rect = e.currentTarget.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        setActiveElement(element);
        setDragOffset({ x: offsetX, y: offsetY });
        setIsDragging(true);

        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !activeElement || !containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const newX = e.clientX - containerRect.left - dragOffset.x;
        const newY = e.clientY - containerRect.top - dragOffset.y;

        // Ensure elements stay within container bounds
        const boundedX = Math.max(0, Math.min(newX, containerRect.width));
        const boundedY = Math.max(0, Math.min(newY, containerRect.height));

        setDesign(prev => ({
            ...prev,
            [`${activeElement.replace('ticket_', '')}_x`]: Math.round(boundedX),
            [`${activeElement.replace('ticket_', '')}_y`]: Math.round(boundedY)
        }));
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setActiveElement(null);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    const handleReset = () => {
        setDesign({
            name_x: 50,
            name_y: 150,
            name_size: 60,
            qr_x: 300,
            qr_y: 50
        });
    };

    const handleSave = async () => {
        if (!currentEvent || !currentEvent.id) return;

        try {
            await api.put(`/organizations/${currentOrganization.id}/events/${currentEvent.id}/`, {
                ticket_design: design
            });
        } catch (error) {
            console.error('Error saving ticket design:', error);
            alert('Failed to save ticket design. Please try again.');
        }
    };

    if (!currentEvent) {
        return (
            <div className="p-6 text-center">
                <p className="text-gray-500">Please select an event to design tickets</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="p-6 text-center">
                <p className="text-gray-500">Loading ticket design...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 text-center">
                <p className="text-red-500">{error}</p>
            </div>
        );
    }

    return (
        <div className="bg-white p-6">
            <h2 className="text-xl font-semibold mb-6">Ticket Design</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Settings panel */}
                <div className="space-y-4">
                    <h3 className="text-lg font-medium border-b pb-2">Design Settings</h3>

                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm">
                        <p className="font-medium">Drag & Drop Instructions:</p>
                        <p>Click and drag any element on the ticket preview to position it as needed.</p>
                    </div>

                    {/* Font Sizes */}
                    <div className="space-y-2 mt-4">
                        <h4 className="font-medium text-gray-700">Font Size</h4>
                        <div>
                            <label className="block text-sm text-gray-700">Name Font Size</label>
                            <input
                                type="number"
                                name="name_size"
                                value={design.name_size}
                                onChange={handleFontSizeChange}
                                className="mt-1 block w-full rounded-md border p-2 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                min="8"
                                max="100"
                            />
                        </div>
                    </div>
                    
                    <button
                        onClick={handleReset}
                        className="w-full mt-4 px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
                    >
                        Reset to Default Positions
                    </button>
                   
                    <button
                        onClick={handleSave}
                        className="w-full mt-4 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Save Design
                    </button>
                </div>

                {/* Preview Section */}
                <div className="border rounded-lg p-4 md:col-span-2 bg-gray-50 overflow-auto">
                    <h3 className="text-lg font-medium border-b pb-2 mb-4">Live Preview (Drag elements to position)</h3>
                    <div
                        ref={containerRef}
                        id="ticket-container"
                        className="relative inline-block border border-gray-300 rounded overflow-hidden"
                        style={{ position: 'relative', display: 'inline-block', border: '1px solid black' }}
                    >
                        <div id="image-container">
                            <img
                                src={ticketImage || "/api/placeholder/800/500"}
                                alt="Ticket Template"
                                id="ticket_image"
                                className="block w-full h-auto"
                            />
                        </div>
                        
                        <div
                            id="ticket-name-overlay"
                            className={`absolute font-bold text-red-600 cursor-move ${activeElement === 'ticket_name' ? 'ring-2 ring-blue-500' : ''}`}
                            style={{
                                position: 'absolute',
                                left: `${design.name_x}px`,
                                top: `${design.name_y}px`,
                                fontSize: `${design.name_size}px`,
                                color: 'red',
                                fontWeight: 'bold',
                                cursor: 'move'
                            }}
                            onMouseDown={(e) => handleMouseDown(e, 'ticket_name')}
                        >
                            Participant Name
                        </div>
                        
                        <img
                            id="ticket-qr-overlay"
                            className={`absolute cursor-move ${activeElement === 'ticket_qr' ? 'ring-2 ring-blue-500' : ''}`}
                            style={{
                                position: 'absolute',
                                left: `${design.qr_x}px`,
                                top: `${design.qr_y}px`,
                                width: '200px',
                                height: '200px',
                                cursor: 'move'
                            }}
                            src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAADIAQMAAACXljzdAAAABlBMVEX///8AAABVwtN+AAAACXBIWXMAAA7EAAAOxAGVKw4bAAAA/ElEQVRYheWXvRGDMAyFxVG49AgehdHMaIziESgpOCv6s0lyyQKSGiM+Gp6eZBvgb1TUaAALL8ny7o8c+sMtUwpbS7fkySMp/KpODW5ezpgkU+lFHfeESx+WjF64APCAX13ihIz5lq9Fuv6Zb2HIE+k9+QgfRDXgRXxQrpW9wabwSNjkRcioczYfRCFqcppvuqPLqEdRxxmhSX4D7qD7tgiipohD7Mg2E+347JGMsM9gRdy3EyIRnXy8m1ET8OSD6RBv5BAFamPH89OqM687JAXtTE7BGizU6Nm6PhzpYJ5AUcwxmfftimcwYr0w1eleiZV+3D7g6wwbgfyNF/UxBAs59J9lAAAAAElFTkSuQmCC"
                            alt="QR Code"
                            onMouseDown={(e) => handleMouseDown(e, 'ticket_qr')}
                        />
                    </div>
                    <p className="mt-4 text-sm text-gray-500">
                        Click and drag elements to position them on the ticket template.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TicketDesign;